import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { ROUTES } from '@/config/routes'
import useSignInClient from '../services/useSignInClient'
import { useToast } from '@/lib/hooks/use-toast'
import CPFInput from '@/components/form/input/cpf-mask-input'

export default function LoginClientFormMinuta() {
  const [cpf, setCpf] = useState('') // Estado para armazenar o CPF
  const [password, setPassword] = useState('') // Estado para armazenar a senha
  const [showPassword, setShowPassword] = useState(false)
  const [isBlinking, setIsBlinking] = useState(false)
  const { signInClient } = useSignInClient() // Pega a função signIn do contexto
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const originalId = searchParams.get('id')
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
    setIsBlinking(true)
    setTimeout(() => setIsBlinking(false), 200)
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    try {
      const client = await signInClient(cpf, password)
      if (client === null) {
        throw new Error('Cliente não encontrado ou não autenticado')
      }

      navigate(`/minuta/${originalId}`)
    } catch (error) {
      console.error(error)
      if (axios.isAxiosError(error)) {
        toast({
          description: error.response?.data?.message || 'Erro de rede',
          variant: 'destructive'
        })
      } else if (error instanceof Error) {
        toast({
          description: error.message || 'Erro de rede',
          variant: 'destructive'
        })
      } else {
        toast({
          description: 'Ocorreu um erro desconhecido',
          variant: 'destructive'
        })
      }
    }
  }

  const handleForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD.path)
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-full max-w-md bg-input dark:bg-input backdrop-blur-sm shadow-xl border-border">
        <form onSubmit={handleSubmit}>
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold text-center text-primary dark:text-primary-hover">Login</CardTitle>
            <CardDescription className="text-center text-foreground dark:text-foreground">
              Coloque seu CPF e senha para entrar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cpf" className="text-sm font-medium text-foreground dark:text-foreground">
                CPF
              </Label>
              <CPFInput
                id="cpf"
                placeholder="000.000.000-00"
                required
                value={cpf}
                onCPFChange={value => setCpf(value)}
                className="bg-input dark:bg-input 
              text-foreground 
               border-border 
               focus:border-primary w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-foreground dark:text-foreground">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  required
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-input dark:bg-input text-foregroundborder-border focus:border-primary"
                />
                <Button
                  type="button"
                  className={`absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent ${isBlinking ? 'animate-pulse' : ''}`}
                  variant="ghost"
                  size="icon"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <Eye className="h-4 w-4 text-primary" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-primary" />
                  )}
                  <span className="sr-only">{showPassword ? 'Ocultar senha' : 'Mostrar senha'}</span>
                </Button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="remember" className="border-primary text-primary" />
              <label htmlFor="remember" className="text-sm font-medium text-foreground dark:text-foreground">
                Lembre-me
              </label>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">
              Entrar
            </Button>
            <Button
              className="w-full text-primary hover:text-primary-hover"
              variant="link"
              onClick={handleForgotPassword}
            >
              Esqueci a senha
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
